import { Component, OnInit, OnDestroy, Injector, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ClrTabs } from '@clr/angular';
import { forkJoin, Subscription } from 'rxjs';

// components
import { DxFormComponent, DxPopupComponent } from 'devextreme-angular';
import { ItemDetailsComponent } from '../../shared/components/item-details/item-details.component';
import { TranslatableFieldComponent } from '../../shared/components/translatable-field/translatable-field.component';

// models
import { ProductPropertyTypeEnum } from '../../shared/enum/productPropertyTypeEnum';
import { UrlTypeEnum} from '../../shared/enum/urlTypeEnum';
import { Language } from '../../shared/models/language';
import { MasterData } from '../../shared/models/master-data';
import { ProductPropertyType } from '../../shared/models/product';

import { LookupTable, LookupTableField } from '../shared/models/lookup-table';

// services
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../shared/services/language.service';

import { LookupTableService } from '../shared/services/lookup-table.service';
import { MasterDataService } from '../shared/services/master-data.service';
import { ProductPropertyTypeService } from '../shared/services/product-property-type.service';


enum LookupTableTypes {
  LOOKUP_TABLE_TYPE_FILTER = 0,
  LOOKUP_TABLE_TYPE_DEFAULT_VALUE = 1
}

@Component({
  selector: 'lookup-table-component',
  templateUrl: 'lookup-table.component.html',
  styleUrls: ['./lookup-table.component.scss']
})
export class LookupTableComponent extends ItemDetailsComponent<LookupTable> implements OnInit, OnDestroy {

  @ViewChild('tabs') tabs: ClrTabs;
  @ViewChild('lookupTableName') lookupTableName: TranslatableFieldComponent;
  @ViewChild(DxPopupComponent) popup: DxPopupComponent;
  @ViewChild('lookupTableForm', { read: DxFormComponent}) lookupTableForm: DxFormComponent;


  auctionClusterId: number;
  languages: Array<Language> = [];
  lookupTableTypes: any = LookupTableTypes;
  productPropertyTypes: Array<ProductPropertyType> = [];
  productPropertyFormats: Array<UrlTypeEnum> = [];
  masterDataLists: Array<MasterData> = [];
  validFields: boolean = false;

  rtlEnabled = localStorage.getItem('last-selected-language-direction') ? JSON.parse(localStorage.getItem('last-selected-language-direction')) : false;
  private _subscription: Subscription;

  get isAnyRowSet(): boolean {
    return this.model && this.model.lookupTableRows && this.model.lookupTableRows.length > 0;
  }

  constructor(
    protected injector: Injector,
    private dataService: LookupTableService,
    private languageService: LanguageService,
    private productPropertyTypeService: ProductPropertyTypeService,
    private masterDataService: MasterDataService,
    private translateService: TranslateService,
    private route: ActivatedRoute
  ) {
    super(injector);
    this.lookupTableTypes = Object.keys(this.lookupTableTypes).filter(f => !isNaN(Number(f))).map(key => ({ name: this.lookupTableTypes[key], value: Number(key) })); // tslint:disable-line:max-line-length
    this._subscription = this.languageService.direction.subscribe(dir => {
      this.rtlEnabled = dir;
    });
  }

  ngOnInit() {
    this.model = new LookupTable();
    this.auctionClusterId = +this.route.snapshot.params['id'];
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  open(lookupTables: Array<LookupTable>, lookupTableId: number) {
    // Autoselect first tab
    // this.tabs.ifActiveService.current = this.tabs.tabsService.children[0].id;

    this.allItems = lookupTables;

    forkJoin(
      this.productPropertyTypeService.getProductProperties(),
      this.productPropertyTypeService.getFormats(ProductPropertyTypeEnum.URL),
      this.masterDataService.getMasterDataLists(this.auctionClusterId, this.translateService.currentLang),
      this.languageService.getAuctionClusterLanguages(this.auctionClusterId)
    ).subscribe(result => {
      this.productPropertyTypes = result[0];
      this.productPropertyFormats= result[1];
      this.masterDataLists = result[2];      
      this.languages = result[3];

      this.spinner.hide();
    },
      error => {
        this.errorService.show(error);
        this.spinner.hide();
      });

    // EDIT
    if (lookupTableId != null) {
      this.isEditMode = true;
      this.spinner.show();
      this.dataService.getLookupTableWithoutRows(this.auctionClusterId, lookupTableId)
        .subscribe((res: LookupTable) => {
          this.model = res;

          this.validFields = this.languageService.checkDefaultLanguageNotEmpty(this.languages, this.model.name);

          if (this.model.resultPropertyTypeId || this.model.resultMasterDataListId) {
            const resultField = new LookupTableField();
            resultField.isResult = true;
            resultField.propertyTypeId = this.model.resultPropertyTypeId || ProductPropertyTypeEnum.MASTER_DATA;
            if (this.model.resultPropertyFormatId) {
              resultField.propertyFormatId = this.model.resultPropertyFormatId;
            }
            if (this.model.resultMasterDataListId) {
              resultField.masterDataListId = this.model.resultMasterDataListId;
            }
            this.model.lookupTableFields.push(resultField);
          }

          this.isOpened = true;
          this.spinner.hide();
        },
          error => {
            this.errorService.show(error);
            this.spinner.hide();
          });
      // NEW
    } else {
      this.model = new LookupTable();
      this.model.lookupTableType = 0;
      this.isEditMode = false;
      this.isOpened = true;

      // Create empty JSON object for translation fields
      const emptyTranslation = {};
      this.languages.forEach(lang => {
        emptyTranslation[lang.code] = '';
      });

      this.model.name = JSON.stringify(emptyTranslation);
    }

    // this.lookupTableName.elementFocus();
  }

  getTranslation(value: string) {
    return this.languageService.getTranslatableText(value);
  }

  save() {
    this.model.name = this.lookupTableName.data;

    /*if (this.model.lookupTableType === 1)*/ {
      let hasDefaultResult = false;
      for (let i = this.model.lookupTableFields.length - 1; i >= 0; i -= 1) {
        // result field for default value type
        if (this.model.lookupTableFields[i].propertyTypeId != null) {
          hasDefaultResult = true;
          if (this.model.lookupTableFields[i].propertyTypeId === ProductPropertyTypeEnum.MASTER_DATA) {
            this.model.resultMasterDataListId = this.model.lookupTableFields[i].masterDataListId;
            this.model.lookupTableFields.splice(i, 1);
          } else {
            this.model.resultPropertyTypeId = this.model.lookupTableFields[i].propertyTypeId;
            this.model.resultPropertyFormatId = this.model.lookupTableFields[i].propertyFormatId;
            this.model.lookupTableFields.splice(i, 1);
          }
        }
      }

      if (!hasDefaultResult) {
        this.model.resultPropertyTypeId = null;
        this.model.resultPropertyFormatId = null;
        this.model.resultMasterDataListId = null;
      }
    }

    if (this.model.lookupTableId) {
      this.isEditMode = true;
    }

    if (this.isEditMode) {
      this.spinner.show();
      this.dataService.edit(this.model)
        .subscribe((res: any) => {
          this.model = res;
          this.model = new LookupTable();
          // this.detailsForm.reset();
          this.close(true);
          this.errorMessage = null;
          this.spinner.hide();
        },
          error => {
            this.errorService.show(error);
            this.spinner.hide();
          });
    } else {
      this.spinner.show();
      this.model.auctionClusterId = this.auctionClusterId;

      // remove fields of another type
      // FILTER
      /*if (this.model.lookupTableType === 0) {
        // if filter type, result is not of property type
        this.model.resultPropertyTypeId = null;
        this.model.resultMasterDataListId = null;
      // DEFAULT VALUE
      } else*/
      if (this.model.lookupTableType === 1) {
        // if default value type, fields cannot be results
        this.model.lookupTableFields.forEach(f => {
          f.isResult = false;
        });
      }

      this.dataService.save(this.model)
        .subscribe((res: any) => {
          this.model = res;
          this.model = new LookupTable();
          // this.detailsForm.reset();
          this.close(true);
          this.errorMessage = null;
          this.spinner.hide();
        },
          error => {
            this.errorService.show(error);
            this.spinner.hide();
          });
    }
  }

  saveWithoutClose() {
    this.model.name = this.lookupTableName.data;

    //let hasDefaultResult = false;

    let hasNonMasterDataResult = false;
    let hasMasterDataResult = false;
    let hasResultProperty = false;

    //filter
    if (this.model.lookupTableType == 0)
    {
      for (let i = this.model.lookupTableFields.length - 1; i >= 0; i -= 1) {
        if (this.model.lookupTableFields[i].isResult || this.model.lookupTableFields[i].propertyTypeId !== ProductPropertyTypeEnum.MASTER_DATA /*this.model.lookupTableFields[i].propertyTypeId != null*/) {
          if (!this.model.lookupTableFields[i].masterDataListId || 
            (this.model.lookupTableFields[i].propertyTypeId && this.model.lookupTableFields[i].propertyTypeId !== ProductPropertyTypeEnum.MASTER_DATA)) {
            this.model.resultPropertyTypeId = this.model.lookupTableFields[i].propertyTypeId;
            this.model.resultPropertyFormatId = this.model.lookupTableFields[i].propertyFormatId;
            this.model.lookupTableFields.splice(i, 1);
            hasNonMasterDataResult = true;
            hasResultProperty = true;
          }
        }
      }
    }
    
    // result field for default value type
    if (this.model.lookupTableType == 1)
    {
      for (let i = this.model.lookupTableFields.length - 1; i >= 0; i -= 1) {
        
        if (this.model.lookupTableFields[i].isResult || 
          (this.model.lookupTableFields[i].propertyTypeId !== ProductPropertyTypeEnum.MASTER_DATA && !this.model.lookupTableFields[i].masterDataListId) /*this.model.lookupTableFields[i].propertyTypeId != null*/) {
          if (this.model.lookupTableFields[i].masterDataListId || this.model.lookupTableFields[i].propertyTypeId === ProductPropertyTypeEnum.MASTER_DATA) {
            this.model.resultMasterDataListId = this.model.lookupTableFields[i].masterDataListId;
            this.model.resultPropertyTypeId = ProductPropertyTypeEnum.MASTER_DATA;
            this.model.resultPropertyFormatId = null;
            this.model.lookupTableFields.splice(i, 1);
            hasMasterDataResult = true;
            hasResultProperty = true;

          } else {
            this.model.resultPropertyTypeId = this.model.lookupTableFields[i].propertyTypeId;
            this.model.resultPropertyFormatId = this.model.lookupTableFields[i].propertyFormatId;
            this.model.resultMasterDataListId = null;
            this.model.lookupTableFields.splice(i, 1);
            hasResultProperty = true;
          }
        }
      }
    }

    if(!hasResultProperty)
    {
      this.model.resultPropertyTypeId = null;
      this.model.resultPropertyFormatId = null;
      this.model.resultMasterDataListId = null;
    }

    //for filter lookups, save only non-masterdata as result type
    /*if(this.model.lookupTableType == 0)
    {
      if(!hasNonMasterDataResult)
        {
          this.model.resultPropertyTypeId = null;
        }
      this.model.resultMasterDataListId = null;
    }*/

    //for default lookups if the result is masterdata, set the result type, else clear the result masterdata id
    /*if(this.model.lookupTableType == 1)
    {
      if(!hasMasterDataResult)
        {
          this.model.resultMasterDataListId = null;
        }
        else {
          this.model.resultPropertyTypeId = ProductPropertyTypeEnum.MASTER_DATA;
        }
    }*/
    

    if (this.model.lookupTableId) {
      this.isEditMode = true;
    }

    if (this.isEditMode) {
      this.spinner.show();
      this.dataService.edit(this.model)
        .subscribe((res: any) => {
          this.model = res;
          if ((this.model.resultPropertyTypeId /*|| this.model.resultMasterDataListId*/)) {
            const resultField = new LookupTableField();
            resultField.isResult = true;
            resultField.propertyTypeId = this.model.resultPropertyTypeId || ProductPropertyTypeEnum.MASTER_DATA;

            if (this.model.resultPropertyFormatId) {
              resultField.propertyFormatId = this.model.resultPropertyFormatId;
            }
            if (this.model.resultMasterDataListId) {
              resultField.masterDataListId = this.model.resultMasterDataListId;
            }
            this.model.lookupTableFields.push(resultField);
          }
          this.errorMessage = null;
          this.spinner.hide();
        },
          error => {
            this.errorService.show(error);
            this.spinner.hide();
          });
    } else {
      this.spinner.show();
      this.model.auctionClusterId = this.auctionClusterId;

      // remove fields of another type
      // FILTER
      if (this.model.lookupTableType === 0) {
        // if filter type, result is not of property type
        this.model.resultPropertyTypeId = null;
        this.model.resultPropertyFormatId = null;
        this.model.resultMasterDataListId = null;
        // DEFAULT VALUE
      } else {
        // if default value type, fields cannot be results
        this.model.lookupTableFields.forEach(f => {
          f.isResult = false;
        });
      }

      this.dataService.save(this.model)
        .subscribe((res: any) => {
          this.model = res;
          if ((this.model.resultPropertyTypeId /*|| this.model.resultMasterDataListId*/)) {
            const resultField = new LookupTableField();
            resultField.isResult = true;
            resultField.propertyTypeId = this.model.resultPropertyTypeId || ProductPropertyTypeEnum.MASTER_DATA;

            if (this.model.resultPropertyFormatId) {
              resultField.propertyFormatId = this.model.resultPropertyFormatId;
            }
            if (this.model.resultMasterDataListId) {
              resultField.masterDataListId = this.model.resultMasterDataListId;
            }
            this.model.lookupTableFields.push(resultField);
          }
          this.errorMessage = null;
          this.spinner.hide();
        },
          error => {
            this.errorService.show(error);
            this.spinner.hide();
          });
    }
  }

  translateSelectBoxes = (item) => {
    if (item) {
      let label = item.name;
      this.translate.get("LOOKUP_TABLE." + item.name).subscribe((res: string) => {
        label  = res;
    });

      return label;
    }
  }

  public onCancel() {
    this.lookupTableName.resetLanguageButtons();
    super.onCancel();
  }

  onChangeTranslationField = (e: any, fieldName: string) => {
    if (fieldName === 'name') {
      this.validFields = this.languageService.checkDefaultLanguageNotEmpty(this.languages, this.lookupTableName.data);
      this.lookupTableForm.instance.updateData(fieldName, this.lookupTableName.data);
    }
  }

  onFieldDataChanged(e: any) {
    if ((e.component._isReady && e.component.NAME !== 'dxPopup') || e.component.NAME === 'dxForm') {
      const result = e.component.validate();
      if (result.brokenRules.length == 0 || this.validFields) {
        document.getElementsByName('btnLookupTableSubmit')[0].removeAttribute('disabled');
      } else {
        document.getElementsByName('btnLookupTableSubmit')[0].setAttribute('disabled', 'disabled');
      }
    } else {
      if (this.isEditMode && this.validFields) {
        document.getElementsByName('btnLookupTableSubmit')[0].removeAttribute('disabled');
      } else {
        document.getElementsByName('btnLookupTableSubmit')[0].setAttribute('disabled', 'disabled');
      }
    }
  }
}
